// src/components/auth/AuthModal.jsx
// Responsive: bottom-sheet on mobile, centered modal on desktop
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { C } from '../../styles/tokens';

export default function AuthModal({ mode, onClose, onSwitch }) {
  const navigate = useNavigate();
  const { registerWithEmail, loginWithEmail, loginWithGoogle, resetPassword } = useAuth();

  const [loading,     setLoading]     = useState(false);
  const [showReset,   setShowReset]   = useState(false);
  const [fullName,    setFullName]    = useState('');
  const [email,       setEmail]       = useState('');
  const [phone,       setPhone]       = useState('');
  const [password,    setPassword]    = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [role,        setRole]        = useState('talent');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [errors,      setErrors]      = useState({});

  const isReg = mode === 'register';

  const validate = () => {
    const errs = {};
    if (isReg) {
      if (fullName.trim().length < 2)      errs.fullName    = 'Full name is required';
      if (!/\S+@\S+\.\S+/.test(email))     errs.email       = 'Invalid email address';
      if (!phone.trim())                   errs.phone       = 'Phone number is required';
      if (password.length < 8)            errs.password    = 'Minimum 8 characters';
      if (password !== confirmPass)       errs.confirmPass = 'Passwords do not match';
      if (!acceptTerms)                   errs.terms       = 'Please accept the terms';
    } else {
      if (!/\S+@\S+\.\S+/.test(email))     errs.email    = 'Invalid email';
      if (!password)                      errs.password = 'Password is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleRegister = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await registerWithEmail(email, password, fullName, phone, role);
      toast.success('Welcome to Oracle Nexus! 🎉');
      onClose();
      navigate(role === 'collaborator' ? '/collaborators' : '/dashboard');
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  const handleLogin = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await loginWithEmail(email, password);
      toast.success('Welcome back! 👋');
      onClose();
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  const handleReset = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) { setErrors({ email: 'Enter a valid email first' }); return; }
    setLoading(true);
    try {
      await resetPassword(email);
      toast.success('Reset link sent! Check your inbox.');
      setShowReset(false);
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  return (
    <>
      <style>{`
        .auth-overlay {
          position: fixed; inset: 0; z-index: 500;
          background: rgba(4,4,15,0.88);
          backdrop-filter: blur(14px);
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: fadeIn 0.2s ease;
          overflow-y: auto;
        }
        .auth-inner {
          width: 100%; max-width: 480px;
          background: #0f0f24;
          border: 1px solid rgba(254,52,232,0.2);
          border-radius: 20px;
          overflow: hidden;
          animation: fadeUp 0.35s cubic-bezier(0.16,1,0.3,1);
          margin: auto;
        }
        @media (max-width: 520px) {
          .auth-overlay { align-items: flex-end !important; padding: 0 !important; }
          .auth-inner {
            max-width: 100% !important;
            border-radius: 20px 20px 0 0 !important;
            max-height: 95vh; overflow-y: auto;
            animation: authSlideUp 0.35s cubic-bezier(0.16,1,0.3,1) !important;
          }
          @keyframes authSlideUp {
            from { transform: translateY(60px); opacity: 0; }
            to   { transform: translateY(0); opacity: 1; }
          }
        }
        .pass-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        @media (max-width: 400px) { .pass-row { grid-template-columns: 1fr !important; } }
        .role-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
      `}</style>

      <div className="auth-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="auth-inner">
          {/* Drag pill (mobile hint) */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 0' }}>
            <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.12)' }} />
          </div>

          {/* Header */}
          <div style={{ padding: '16px 22px 0', position: 'relative' }}>
            <button onClick={onClose} style={{ position: 'absolute', top: '12px', right: '16px', background: 'rgba(255,255,255,0.07)', border: 'none', color: 'white', width: '30px', height: '30px', borderRadius: '7px', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '3px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '5px', background: `linear-gradient(135deg,${C.magenta},${C.violet})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' }}>⬡</div>
              <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: '12px' }}>Oracle Nexus</span>
            </div>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: 'clamp(18px,5vw,21px)', fontWeight: 800, marginBottom: '2px' }}>
              {showReset ? 'Reset Password' : isReg ? 'Create Your Account' : 'Welcome Back'}
            </h2>
            <p style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.38)', marginBottom: '16px' }}>
              {showReset ? "We'll send a reset link to your email" : isReg ? 'Join the future of talent & opportunity' : 'Sign in to your Oracle Nexus account'}
            </p>
          </div>

          {/* Body */}
          <div style={{ padding: '0 22px 28px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {/* RESET */}
            {showReset && (
              <>
                <Input label="Email Address" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} error={errors.email} />
                <Button fullWidth loading={loading} onClick={handleReset}>Send Reset Link →</Button>
                <p style={{ textAlign: 'center', fontSize: '12.5px', color: 'rgba(255,255,255,0.36)' }}>
                  <span style={{ color: C.magenta, cursor: 'pointer' }} onClick={() => setShowReset(false)}>← Back to login</span>
                </p>
              </>
            )}

            {/* REGISTER */}
            {!showReset && isReg && (
              <>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: '7px' }}>I am joining as</label>
                  <div className="role-row">
                    {[
                      { v: 'talent',       icon: '🎯', label: 'Talent',   sub: 'Find opportunities' },
                      { v: 'collaborator', icon: '🏢', label: 'Company',  sub: 'Hire talent' },
                    ].map(r => (
                      <button key={r.v} onClick={() => setRole(r.v)} style={{ padding: '12px', borderRadius: '10px', background: role === r.v ? 'rgba(254,52,232,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${role === r.v ? C.magenta : 'rgba(255,255,255,0.08)'}`, color: '#fff', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}>
                        <div style={{ fontSize: '17px', marginBottom: '3px' }}>{r.icon}</div>
                        <div style={{ fontSize: '13px', fontWeight: 700, fontFamily: "'Syne',sans-serif" }}>{r.label}</div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '1px' }}>{r.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <Input label="Full Name" placeholder="e.g. Alex Johnson" value={fullName} onChange={e => setFullName(e.target.value)} error={errors.fullName} />
                <Input label="Email Address" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} error={errors.email} />
                <Input label="Phone Number" type="tel" placeholder="+234 800 000 0000" value={phone} onChange={e => setPhone(e.target.value)} error={errors.phone} hint="Include country code (e.g. +234)" />

                <div className="pass-row">
                  <Input label="Password" type="password" placeholder="Min. 8 chars" value={password} onChange={e => setPassword(e.target.value)} error={errors.password} />
                  <Input label="Confirm" type="password" placeholder="Repeat" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} error={errors.confirmPass} />
                </div>

                <div style={{ padding: '11px 13px', borderRadius: '9px', background: 'rgba(0,208,132,0.08)', border: '1px solid rgba(0,208,132,0.2)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px', flexShrink: 0 }}>🎁</span>
                  <span style={{ fontSize: '12.5px', color: '#4ade80', lineHeight: 1.4 }}><strong>100 ONT</strong> welcome bonus on signup!</span>
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <input type="checkbox" id="tos" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} style={{ marginTop: '2px', accentColor: C.magenta, cursor: 'pointer', flexShrink: 0, width: '16px', height: '16px' }} />
                  <label htmlFor="tos" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.42)', lineHeight: 1.5, cursor: 'pointer' }}>
                    I agree to the <span style={{ color: C.magenta }}>Terms of Service</span> and <span style={{ color: C.magenta }}>Privacy Policy</span>
                  </label>
                </div>
                {errors.terms && <p style={{ fontSize: '12px', color: C.danger, marginTop: '-6px' }}>{errors.terms}</p>}

                <Button fullWidth loading={loading} onClick={handleRegister}>Create Account →</Button>
              </>
            )}

            {/* LOGIN */}
            {!showReset && !isReg && (
              <>
                <Input label="Email Address" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} error={errors.email} />
                <div>
                  <Input label="Password" type="password" placeholder="••••••••••" value={password} onChange={e => setPassword(e.target.value)} error={errors.password} />
                  <div style={{ textAlign: 'right', marginTop: '6px' }}>
                    <span style={{ fontSize: '12px', color: C.magenta, cursor: 'pointer' }} onClick={() => setShowReset(true)}>Forgot password?</span>
                  </div>
                </div>
                <Button fullWidth loading={loading} onClick={handleLogin}>Sign In →</Button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.28)' }}>or</span>
                  <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                </div>
                <button onClick={async () => { setLoading(true); try { await loginWithGoogle(); toast.success('Signed in!'); onClose(); navigate('/dashboard'); } catch(e) { toast.error(e.message); } finally { setLoading(false); } }} style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '9px', color: 'white', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  🔗 Continue with Google
                </button>
              </>
            )}

            {!showReset && (
              <p style={{ textAlign: 'center', fontSize: '12.5px', color: 'rgba(255,255,255,0.36)' }}>
                {isReg ? 'Already have an account? ' : 'New to Oracle Nexus? '}
                <span style={{ color: C.magenta, cursor: 'pointer', fontWeight: 600 }} onClick={onSwitch}>
                  {isReg ? 'Sign In' : 'Create Account'}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
